import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Lock, Unlock, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SRC = { sablon: "Şablon", ai: "Yapay zekâ", manuel: "Manuel" };
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border rounded-xl p-4" data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">{value ?? "—"}</p>
    </div>
  );
}

function EditRow({ item, kind, onSaved }) {
  const [f, setF] = useState({ seo_title: item.seo_title || "", seo_description: item.seo_description || "" });
  const id = kind === "products" ? item.urun_id : item.kategori_id;
  const save = (extra = {}) => api.patch(`/admin/seo/${kind}/${id}`, extra)
    .then(() => { toast.success("Kaydedildi"); onSaved(); }).catch(err);
  return (
    <div className="bg-white border rounded-xl p-4" data-testid={`seo-row-${id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="font-medium text-slate-900 text-sm">{item.urun_adi_tr || item.kategori_yolu}</span>
        {item.urun_kodu && <Badge variant="outline" className="font-mono text-[10px]">{item.urun_kodu}</Badge>}
        <Badge variant="secondary" className="text-[10px]">{SRC[item.seo_source] || "—"}</Badge>
        {item.seo_degisti && <Badge className="bg-amber-100 text-amber-800 text-[10px] hover:bg-amber-100">Veri değişti</Badge>}
        {item.seo_locked && <Badge className="bg-slate-800 text-[10px]">Kilitli</Badge>}
        <span className="text-[11px] text-slate-400 font-mono ml-auto break-all">/{kind === "products" ? "urun" : "kategori"}/{item.seo_slug}</span>
      </div>
      <Input value={f.seo_title} onChange={(e) => setF({ ...f, seo_title: e.target.value })} className="text-sm mb-1"
        data-testid={`seo-title-${id}`} />
      <p className={`text-[11px] mb-2 ${f.seo_title.length > 60 ? "text-red-600" : "text-slate-400"}`}>{f.seo_title.length}/60</p>
      <Textarea value={f.seo_description} onChange={(e) => setF({ ...f, seo_description: e.target.value })}
        className="text-sm min-h-[60px]" data-testid={`seo-desc-${id}`} />
      <p className={`text-[11px] ${f.seo_description.length > 160 ? "text-red-600" : "text-slate-400"}`}>{f.seo_description.length}/160</p>
      <div className="flex gap-2 mt-2">
        <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => save(f)} data-testid={`seo-save-${id}`}>Kaydet</Button>
        <Button size="sm" variant="outline" onClick={() => save({ seo_locked: !item.seo_locked })} data-testid={`seo-lock-${id}`}>
          {item.seo_locked ? <><Unlock className="w-3.5 h-3.5 mr-1" />Kilidi aç</> : <><Lock className="w-3.5 h-3.5 mr-1" />Kilitle</>}
        </Button>
      </div>
    </div>
  );
}

export default function AdminSeo() {
  const [ov, setOv] = useState(null);
  const [tab, setTab] = useState("products");
  const [list, setList] = useState({ items: [], pages: 1, total: 0 });
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [durum, setDurum] = useState("");
  const [page, setPage] = useState(1);
  const [running, setRunning] = useState(false);

  const loadOv = () => api.get("/admin/seo/overview").then((r) => setOv(r.data));
  const loadList = useCallback(() => {
    if (tab === "products") {
      api.get(`/admin/seo/products?${new URLSearchParams({ q, durum, page })}`).then((r) => setList(r.data));
    } else api.get("/admin/seo/categories").then((r) => setCats(r.data.items || []));
  }, [tab, q, durum, page]);
  useEffect(() => { loadOv(); }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const runAi = async () => {
    const n = Math.min(ov.ai_bekleyen, ov.ai_kullanim.toplu_sinir, ov.ai_kullanim.kalan);
    if (!window.confirm(`${n} kayıt yapay zekâ ile işlenecek (bu ay kalan: ${ov.ai_kullanim.kalan}). Devam edilsin mi?`)) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/seo/ai-batch", { limit: ov.ai_kullanim.toplu_sinir });
      toast.success(`${r.data.islenen} kayıt işlendi${r.data.hata ? `, ${r.data.hata} hata` : ""}`);
      loadOv(); loadList();
    } catch (e) { err(e); } finally { setRunning(false); }
  };

  const u = ov?.ai_kullanim;
  return (
    <div className="max-w-5xl" data-testid="admin-seo-page">
      <h1 className="font-head text-2xl font-bold text-slate-900 mb-1">SEO Yönetimi</h1>
      <p className="text-sm text-slate-500 mb-6">
        Başlık ve açıklamalar ürün verilerinden otomatik oluşturulur; yeni veya değişen kayıtlar kendiliğinden güncellenir.
        Yapay zekâ yalnızca istediğinizde, yeni/değişen kayıtlar için çalışır. Kilitli ve elle düzenlenen kayıtlara dokunulmaz.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Yayındaki ürün" value={ov?.aktif_urun} testid="seo-stat-active" />
        <Stat label="Şablon / Yapay zekâ / Manuel" value={ov && `${ov.sablon} / ${ov.ai} / ${ov.manuel}`} testid="seo-stat-sources" />
        <Stat label="Kilitli" value={ov?.kilitli} testid="seo-stat-locked" />
        <Stat label="Yapay zekâ bekleyen" value={ov?.ai_bekleyen} testid="seo-stat-pending" />
      </div>

      <section className="bg-white border rounded-xl p-5 mb-6" data-testid="seo-ai-box">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h2 className="font-head font-semibold text-slate-900">Yapay Zekâ ile SEO Taslağı</h2>
            <p className="text-xs text-slate-500 mt-1" data-testid="seo-ai-usage">
              Platform kullanımı (yapay zekâ) — {u?.ay}: {u?.kullanilan ?? 0} / {u?.aylik_sinir} kayıt · tek seferde en fazla {u?.toplu_sinir}.
              Bu kullanım reklam harcamasından ayrıdır.
            </p>
          </div>
          <Button onClick={runAi} disabled={running || !ov?.ai_bekleyen || !u?.kalan}
            className="bg-teal-600 hover:bg-teal-700" data-testid="seo-ai-run">
            <Sparkles className="w-4 h-4 mr-1" />{running ? "İşleniyor..." : "Yeni/değişen kayıtları işle"}
          </Button>
        </div>
      </section>

      <section className="bg-slate-50 border rounded-xl p-4 mb-6 text-xs text-slate-600 space-y-1" data-testid="seo-tech-box">
        <p><b>Site haritası:</b> <a className="text-teal-700 underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} target="_blank" rel="noreferrer">/api/sitemap.xml</a> (statik sayfalar, ürünlü kategoriler ve yayındaki ürünler; filtre kombinasyonları eklenmez)</p>
        <p><b>robots.txt:</b> <a className="text-teal-700 underline" href="/robots.txt" target="_blank" rel="noreferrer">/robots.txt</a> — yönetim, hesap ve teklif sayfaları taramaya kapalı; site haritası adresi yayın sırasında otomatik yazılır.</p>
        <p><b>Filtreli/arama sonuç sayfaları</b> “noindex, follow” ile işaretlenir ve kategori ya da katalog sayfasına canonical verir.</p>
        <p>Fiyat, stok veya değerlendirme verisi yapılandırılmış veriye eklenmez (teklif usulü satış).</p>
      </section>

      <div className="flex gap-2 mb-4">
        {[["products", "Ürünler"], ["categories", "Kategoriler"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} onClick={() => setTab(k)}
            className={tab === k ? "bg-slate-900" : ""} data-testid={`seo-tab-${k}`}>{l}</Button>
        ))}
      </div>

      {tab === "products" ? (
        <>
          <div className="flex flex-wrap gap-2 mb-4">
            <Input placeholder="Ürün ara" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="max-w-xs" data-testid="seo-search" />
            <select value={durum} onChange={(e) => { setDurum(e.target.value); setPage(1); }} className="border rounded-md px-2 text-sm" data-testid="seo-filter">
              <option value="">Tümü</option><option value="sablon">Şablon</option><option value="ai">Yapay zekâ</option>
              <option value="manuel">Manuel</option><option value="kilitli">Kilitli</option><option value="degisen">Veri değişen</option>
            </select>
            <span className="text-xs text-slate-500 self-center">{list.total} kayıt</span>
          </div>
          <div className="space-y-3">
            {list.items.map((it) => <EditRow key={it.urun_id} item={it} kind="products" onSaved={() => { loadList(); loadOv(); }} />)}
          </div>
          <div className="flex gap-2 items-center mt-4">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="seo-prev">Önceki</Button>
            <span className="text-xs text-slate-500">{page} / {list.pages}</span>
            <Button size="sm" variant="outline" disabled={page >= list.pages} onClick={() => setPage(page + 1)} data-testid="seo-next">Sonraki</Button>
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {cats.map((c) => <EditRow key={c.kategori_id} item={c} kind="categories" onSaved={loadList} />)}
        </div>
      )}
    </div>
  );
}
